// One ambient effect, on its own clear canvas over the backdrop. It sizes
// itself to what it covers at the resolution its effect asks for, lands on the
// picture with its effect's blend, runs at its effect's frame rate, and stops
// while the tab is hidden. Whoever shows it decides whether motion is allowed:
// with motion off there is no layer at all, rather than one frozen mid-fall.

import { useEffect, useRef } from "react";
import type { ThemeEffect } from "../../shared/types";
import { DRAWN, type EffectEnv, type PictureMap } from "./effects";
import { hex } from "./scenes";
import { useStore } from "../state/store";

export function EffectLayer({ effect, map }: { effect: ThemeEffect; map: PictureMap | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const draw = effect.kind === "drift" ? null : DRAWN[effect.kind];
  const phone = useStore((s) => s.mobile);
  useEffect(() => {
    const el = ref.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx || !draw) return;
    let env: EffectEnv | null = null;
    let state: unknown = null;
    let raf = 0;
    let last = 0;
    let size = "";
    const t0 = performance.now();
    const setup = () => {
      const box = el.getBoundingClientRect();
      const cw = Math.max(1, box.width);
      const ch = Math.max(1, box.height);
      // a phone draws its effects at one pixel to a point and at most 30 frames a second, to spare its battery
      const dpr = phone ? 1 : Math.min(window.devicePixelRatio || 1, cw * ch > 3_000_000 ? 1 : 1.5);
      const w = Math.max(2, Math.round(cw * dpr * draw.res));
      const h = Math.max(2, Math.round(ch * dpr * draw.res));
      if (`${w}x${h}` === size) return;
      size = `${w}x${h}`;
      el.width = w;
      el.height = h;
      env = { w, h, u: w / 1600, strength: effect.strength ?? 0.5, color: effect.color ? hex(effect.color) : null, map, seed: 7, x: effect.x, y: effect.y };
      state = draw.build(env);
    };
    const paint = (now: number) => env && draw.frame(ctx, (now - t0) / 1000, state, env);
    setup();
    paint(performance.now());
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (document.hidden || now - last < 1000 / (phone ? Math.min(draw.fps, 30) : draw.fps) - 2) return;
      last = now;
      paint(now);
    };
    raf = requestAnimationFrame(loop);
    let rt: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
      if (rt) clearTimeout(rt);
      rt = setTimeout(setup, 150);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (rt) clearTimeout(rt);
    };
  }, [draw, effect.strength, effect.color, effect.x, effect.y, map, phone]);
  if (!draw) return null;
  return <canvas ref={ref} className="backdrop-effect" data-effect={effect.kind} style={draw.blend === "normal" ? undefined : { mixBlendMode: draw.blend }} />;
}
